import { Box, IconButton, Typography, useTheme } from '@mui/material'
import React from 'react'

import { Icons } from '../../utils/icons.jsx'
import CustomTooltip from './CustomTooltip.jsx'

const CustomChip = ( {
    containerStyles = {},
    disabled = false,
    chipStyles = {},
    textStyles = {},
    tooltipPlacement = "top",
    textVariant,
    startIcon,
    endIcon,
    color,
    value,
    onClick,
    onEndIconClick,
    variant = "outlined",
    tooltipTitle = "",
    gradient = `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`,
    count,
    endButtonSx,
    ...rest
} ) => {

    const { palette, theme, alpha } = useTheme()

    const clickableEndIcon = onEndIconClick && typeof onEndIconClick === 'function'
    const clickable = onClick && typeof onClick === 'function'

    if ( !value )
        return null

    if ( variant === "outlined" )
        return (
            <Box
                {...rest}
                onClick={( clickable && !disabled ) ? onClick : undefined}
                sx={{ width: "fit-content", display: "inline-block", position: "relative", borderRadius: 5, ...containerStyles, }}
            >
                <CustomTooltip disableInteractive placement="top" sx={{ zIndex: 400000 }} title={tooltipTitle ?? ""}>
                    <Box
                        component={clickable ? "button" : "div"}
                        sx={{
                            background: "none",
                            display: "flex",
                            width: "fit-content",
                            gap: "5px",
                            alignItems: "center",
                            padding: "0px 5px",
                            borderRadius: "2rem",
                            border: `1px solid ${palette[color] ? palette[color].main + "55" : ( color || palette.text.primary ) + "55"}`,
                            color: palette[color] ? palette[color].main : color || palette.text.primary,
                            fontSize: "12px",
                            cursor: clickable ? "pointer" : "inherit",
                            ":hover": {
                                background: clickable ? palette.action.hover : "none"
                            },
                            ...chipStyles
                        }}
                    >
                        {startIcon}
                        <Typography
                            variant={textVariant || 'caption'}
                            noWrap
                            sx={{
                                display: "flex",
                                color: palette[color] ? palette[color].main : color || palette.text.primary,
                                fontSize: "12px",
                                ...textStyles
                            }}
                        >
                            {value}
                        </Typography>
                        {count && <Typography
                            variant={textVariant || 'caption'}
                            sx={{
                                color: palette[color] ? palette[color].main : color || palette.text.primary,
                                fontSize: "10px",
                                borderRadius: "50px",
                                minWidth: "20px",
                                margin: "2px 0 2px 0",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                border: `1px solid ${palette[color] ? palette[color].main + "55" : ( color || palette.text.primary ) + "55"}`,
                                ...textStyles
                            }}
                        >
                            {count}
                        </Typography>}
                        {clickableEndIcon && <IconButton onClick={onEndIconClick} disabled={disabled} size='small' sx={{ fontSize: "14px", m: "2px", p: 0, ...( endButtonSx || {} ) }} >{endIcon || Icons.default.CancelIcon}</IconButton>}
                        {!clickableEndIcon && endIcon && endIcon}
                    </Box>
                </CustomTooltip>
            </Box>
        )

    if ( variant === "semifilled" ) {
        const bg = palette[color] ? palette[color].light + 22 : color ? alpha( color, 0.3 ) : theme === 'dark' ? palette.grey[700] : palette.grey[400]
        return (
            <Box
                {...rest}
                onClick={( clickable && !disabled ) ? onClick : undefined}
                sx={{ width: "fit-content", display: "inline-block", position: "relative", borderRadius: 5, ...containerStyles, }}
            >
                <CustomTooltip disableInteractive placement="top" sx={{ zIndex: 400000 }} title={tooltipTitle ?? ""}>
                    <Box
                        component={clickable ? "button" : "div"}
                        sx={{
                            background: "none",
                            display: "flex",
                            width: "fit-content",
                            gap: "5px",
                            alignItems: "center",
                            padding: "0px 5px",
                            borderRadius: "2rem",
                            border: `1px solid ${palette[color] ? palette[color].main + "55" : ( color || palette.text.primary ) + "55"}`,
                            bgcolor: bg,
                            color: palette[color] ? palette[color].main : color || palette.text.primary,
                            fontSize: "12px",
                            cursor: clickable ? "pointer" : "inherit",
                            ":hover": {
                                background: clickable ? palette.action.hover : bg
                            },
                            ...chipStyles
                        }}
                    >
                        {startIcon}
                        <Typography
                            variant={textVariant || 'caption'}
                            noWrap
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                color: palette[color] ? palette[color].main : color || palette.text.primary,
                                fontSize: "12px",
                                ...textStyles
                            }}
                        >
                            {value}
                        </Typography>
                        {count && <Typography
                            variant={textVariant || 'caption'}
                            sx={{
                                color: palette[color] ? palette[color].main : color || palette.text.primary,
                                fontSize: "10px",
                                borderRadius: "50px",
                                minWidth: "20px",
                                margin: "2px 0 2px 0",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                border: `1px solid ${palette[color] ? palette[color].main + "55" : ( color || palette.text.primary ) + "55"}`,
                                ...textStyles
                            }}
                        >
                            {count}
                        </Typography>}
                        {clickableEndIcon && <IconButton onClick={onEndIconClick} disabled={disabled} size='small' sx={{ fontSize: "14px", m: "2px", p: 0, ...( endButtonSx || {} ) }} >{endIcon || Icons.default.CancelIcon}</IconButton>}
                        {!clickableEndIcon && endIcon && endIcon}
                    </Box>
                </CustomTooltip>
            </Box>
        )
    }
    if ( variant === "filled" ) {

        const bgColor = palette[color] ? palette[color].main : color ? color : theme === 'dark' ? palette.grey[900] : palette.grey[300]
        const borderColor = palette[color] ? palette[color].main + "55" : color ? color : palette.borderColor
        const textColor = palette[color] ? palette[color].contrastText : palette.getContrastText( palette[color] ? palette[color].main : color ? color : theme === 'dark' ? palette.grey[700] : palette.grey[400] )

        return (
            <Box
                {...rest}
                onClick={( clickable && !disabled ) ? onClick : undefined}
                sx={{ width: "fit-content", display: "inline-block", position: "relative", borderRadius: 5, ...containerStyles, }}
            >
                <CustomTooltip disableInteractive placement={tooltipPlacement} sx={{ zIndex: 400000 }} title={tooltipTitle ?? ""}>
                    <Box
                        component={clickable ? "button" : "div"}
                        sx={{
                            display: "flex",
                            width: "fit-content",
                            gap: "5px",
                            alignItems: "center",
                            padding: "0px 5px",
                            borderRadius: "2rem",
                            border: `1px solid ${borderColor}`,
                            bgcolor: bgColor,
                            color: textColor,
                            fontSize: "12px",
                            cursor: clickable ? "pointer" : "inherit",
                            ":hover": {
                                background: clickable ? palette.action.hover : bgColor,
                            },
                            ...chipStyles
                        }}
                    >
                        {startIcon}
                        <Typography
                            variant={textVariant || 'caption'}
                            noWrap
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                color: palette[color] ? palette[color].contrastText : color || palette.text.primary,
                                fontSize: "12px",
                                ...textStyles
                            }}
                        >
                            {value}
                        </Typography>
                        {count && <Typography
                            variant={textVariant || 'caption'}
                            sx={{
                                color: palette[color] ? palette[color].main : color || palette.text.primary,
                                fontSize: "10px",
                                borderRadius: "50px",
                                minWidth: "20px",
                                margin: "2px 0 2px 0",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                border: `1px solid ${palette[color] ? palette[color].main + "55" : ( color || palette.text.primary ) + "55"}`,
                                ...textStyles
                            }}
                        >
                            {count}
                        </Typography>}
                        {clickableEndIcon && <IconButton onClick={onEndIconClick} disabled={disabled} size='small' sx={{ fontSize: "14px", m: "2px", p: 0, ...( endButtonSx || {} ) }} >{endIcon || Icons.default.CancelIcon}</IconButton>}
                        {!clickableEndIcon && endIcon && endIcon}
                    </Box>
                </CustomTooltip>
            </Box>
        )
    }
    if ( variant === "gradient" )
        return (
            <Box
                {...rest}
                onClick={( clickable && !disabled ) ? onClick : undefined}
                sx={{ width: "fit-content", display: "inline-block", position: "relative", borderRadius: 5, ...containerStyles, }}
            >
                <CustomTooltip disableInteractive placement={tooltipPlacement} sx={{ zIndex: 400000 }} title={tooltipTitle ?? ""}>
                    <Box
                        component={clickable ? "button" : "div"}
                        sx={{
                            display: "flex",
                            width: "fit-content",
                            gap: "5px",
                            alignItems: "center",
                            padding: "0px 5px",
                            borderRadius: "2rem",
                            border: `1px solid ${palette[color] ? palette[color].main + "55" : theme === 'dark' ? palette.grey[700] : palette.grey[400]}`,
                            background: gradient,
                            color: palette[color] ? palette[color].contrastText : color || palette.text.primary,
                            fontSize: "12px",
                            cursor: clickable ? "pointer" : "inherit",
                            ":hover": {
                                background: palette.action.hover
                            },
                            ...chipStyles
                        }}
                    >
                        {startIcon}
                        <Typography
                            variant={textVariant || 'caption'}
                            noWrap
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                color: palette[color] ? palette[color].contrastText : color || palette.text.primary,
                                fontSize: "12px",
                                ...textStyles
                            }}
                        >
                            {value}
                        </Typography>
                        {clickableEndIcon && <IconButton onClick={onEndIconClick} disabled={disabled} size='small' sx={{ fontSize: "14px", m: "2px", p: 0, ...( endButtonSx || {} ) }} >{endIcon || Icons.default.CancelIcon}</IconButton>}
                        {!clickableEndIcon && endIcon}
                    </Box>
                </CustomTooltip>
            </Box>
        )
}

export default CustomChip
